import { useMemo } from 'react';
import { useThreadsState } from '../contexts/ThreadsContext';

// 선택적 구독을 위한 기본 훅
export const useThreadsSelector = (selector) => {
  const state = useThreadsState();
  return useMemo(() => selector(state), [state, selector]);
};

// 섹션별 전용 선택자 훅들 - 새로운 섹션별 데이터 모델 기반

/**
 * 성과 요약 섹션용 데이터 Hook
 * sectionDataModels.performanceSummary 사용
 */
export const usePerformanceSummaryData = () => {
  return useThreadsSelector(state => {
    const sectionData = state.sectionDataModels?.performanceSummary || {};
    
    // 에러시 기본값 제공
    const defaultInsight = {
      followers: 0,
      likes: 0, 
      replies: 0,
      reposts: 0,
      numThreads: 0,
      profileViews: []
    };
    
    return {
      lifetimeInsight: sectionData.metrics || defaultInsight,
      profile: sectionData.profile || null,
      isInsightsLoading: !sectionData.isReady,  // 🔥 개별 섹션 기준으로 수정
      isDataReady: sectionData.isReady || false,
      error: sectionData.error || state.error,
      lastUpdated: sectionData.lastUpdated
    };
  });
};

/**
 * 키 메트릭 차트 섹션용 데이터 Hook  
 * sectionDataModels.keyMetricChart 사용
 */
export const useKeyMetricChartData = () => {
  return useThreadsSelector(state => {
    const sectionData = state.sectionDataModels?.keyMetricChart || {};
    
    // 에러시 기본값 제공
    const defaultInsight = {
      profileViews: [],
      profileViewsTotal: 0
    };
    const defaultWeeklyEngagement = [];
    
    // profileViews 데이터 유효성 검사
    const hasValidProfileViews = sectionData.insights?.profileViews && 
                                 Array.isArray(sectionData.insights.profileViews);
    
    return {
      lifetimeInsight: sectionData.insights || defaultInsight,
      lifetimeWeeklyEngagement: sectionData.weeklyEngagement || defaultWeeklyEngagement,
      isInsightsLoading: !sectionData.isReady,  // 🔥 개별 섹션 기준으로 수정
      isDataReady: sectionData.isReady || false,
      error: sectionData.error || state.error,
      lastUpdated: sectionData.lastUpdated,
      hasValidProfileViews
    };
  });
};

/**
 * 스레드 성능 개요 섹션용 데이터 Hook
 * sectionDataModels.threadPerformanceOverview 사용
 */
export const useThreadPerformanceData = () => {
  return useThreadsSelector(state => {
    const sectionData = state.sectionDataModels?.threadPerformanceOverview || {};
    
    // 기본값 - 항상 완전한 구조 제공
    const defaultBoxPlotData = {
      views: { min: 0, max: 0, avg: 0 },
      likes: { min: 0, max: 0, avg: 0 },
      replies: { min: 0, max: 0, avg: 0 },
      reposts: { min: 0, max: 0, avg: 0 }
    };
    
    // boxPlotData가 완전한 구조를 가지고 있는지 확인
    const isValidBoxPlotData = (data) => {
      if (!data || typeof data !== 'object') return false;
      const requiredMetrics = ['views', 'likes', 'replies', 'reposts'];
      return requiredMetrics.every(metric => 
        data[metric] && 
        typeof data[metric] === 'object' &&
        typeof data[metric].min === 'number' &&
        typeof data[metric].max === 'number' &&
        typeof data[metric].avg === 'number'
      );
    };
    
    const finalBoxPlotData = isValidBoxPlotData(sectionData.boxPlotData) 
      ? sectionData.boxPlotData 
      : defaultBoxPlotData;
    
    return {
      lifetimeThreadsList: sectionData.threads || [],
      boxPlotData: finalBoxPlotData,
      isThreadsLoading: !sectionData.isReady,  // 🔥 개별 섹션 기준으로 수정
      isProcessing: !sectionData.isReady,      // 🔥 개별 섹션 기준으로 수정
      isDataReady: sectionData.isReady || false,
      error: sectionData.error || state.error,
      lastUpdated: sectionData.lastUpdated
    };
  });
};

/**
 * 상위 성과 스레드 섹션용 데이터 Hook
 * sectionDataModels.topPerformanceThreads 사용
 */
export const useTopPerformanceData = () => {
  return useThreadsSelector(state => {
    const sectionData = state.sectionDataModels?.topPerformanceThreads || {};
    
    return {
      lifetimeThreadsList: sectionData.threads || [],
      isThreadsLoading: !sectionData.isReady,  // 🔥 개별 섹션 기준으로 수정
      isDataReady: sectionData.isReady || false,
      error: sectionData.error || state.error,
      lastUpdated: sectionData.lastUpdated
    };
  });
};

/**
 * 친구 상호작용 섹션용 데이터 Hook
 * sectionDataModels.friendsInteraction 사용
 */
export const useFriendsInteractionData = () => {
  return useThreadsSelector(state => {
    const sectionData = state.sectionDataModels?.friendsInteraction || {};
    
    return {
      repliesByUser: sectionData.repliesByUser || [],
      lifetimeReplies: sectionData.lifetimeReplies || [],
      isRepliesLoading: !sectionData.isReady,  // 🔥 개별 섹션 기준으로 수정
      isProcessing: !sectionData.isReady,      // 🔥 개별 섹션 기준으로 수정
      isDataReady: sectionData.isReady || false,
      error: sectionData.error || state.error,
      lastUpdated: sectionData.lastUpdated
    };
  });
};

// === 추가 유틸리티 훅 ===

/**
 * 모든 섹션의 준비 상태를 확인하는 훅
 */
export const useAllSectionsStatus = () => {
  return useThreadsSelector(state => {
    const sections = state.sectionDataModels || {};
    
    return {
      topPerformanceThreads: sections.topPerformanceThreads?.isReady || false,
      performanceSummary: sections.performanceSummary?.isReady || false,
      threadPerformanceOverview: sections.threadPerformanceOverview?.isReady || false,
      keyMetricChart: sections.keyMetricChart?.isReady || false,
      friendsInteraction: sections.friendsInteraction?.isReady || false,
      
      // 전체 상태
      allReady: Object.values(sections).every(section => section?.isReady || false),
      anyReady: Object.values(sections).some(section => section?.isReady || false),
      readyCount: Object.values(sections).filter(section => section?.isReady || false).length,
      totalCount: Object.keys(sections).length
    };
  });
};

/**
 * 특정 섹션의 상세 상태를 확인하는 훅
 */
export const useSectionStatus = (sectionName) => {
  return useThreadsSelector(state => {
    const sectionData = state.sectionDataModels?.[sectionName] || {};
    
    return {
      isReady: sectionData.isReady || false,
      error: sectionData.error || null,
      lastUpdated: sectionData.lastUpdated || null,
      hasData: sectionData.isReady && !sectionData.error
    };
  });
};

/**
 * 레거시 호환성을 위한 기존 방식 훅 (Deprecated)
 * @deprecated 새로운 섹션별 훅 사용 권장
 */
export const useLegacyLifetimeData = () => {
  return useThreadsSelector(state => {
    console.warn('useLegacyLifetimeData is deprecated. Use specific section hooks instead.');
    
    return {
      lifetimeThreadsList: state.lifetimeThreadsList || [],
      lifetimeInsight: state.lifetimeInsight || null,
      lifetimeWeeklyEngagement: state.lifetimeWeeklyEngagement || [],
      lifetimeReplies: state.lifetimeReplies || [],
      repliesByUser: state.repliesByUser || [],
      boxPlotData: state.boxPlotData || {},
      isLifetimeLoading: state.isLifetimeLoading,
      isLifetimeReady: state.isLifetimeReady,
      error: state.error
    };
  });
}; 